import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, RotateCcw, Upload } from 'lucide-react';
import api from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthContext';
import Breadcrumb from '@/shared/components/Breadcrumb';
import { Button } from '@/shared/ui/button';
import CouvertureDesChamps from '../components/CouvertureDesChamps';
import ListeDesGabarits from '../components/ListeDesGabarits';
import RattachementAgregateur from '../components/RattachementAgregateur';
import LienInscriptionAgregateur from '../components/LienInscriptionAgregateur';
import usePagesDuGabarit from '../usePagesDuGabarit';
import PlanDeGabarit from '../components/PlanDeGabarit';
import RenduRempli from '../components/RenduRempli';
import ValeursDeLApercu from '../components/ValeursDeLApercu';

/**
 * L'ATELIER DU MODÈLE DE MANDAT — le plan au centre, en grand.
 *
 * Refonte demandée par Boubacar le 01/09, puis élargie le 02/09 pour porter le
 * glisser-déposer des champs. Ce fichier n'est plus qu'un ASSEMBLAGE : la liste, le plan,
 * les valeurs et la couverture vivent chacun dans leur composant. Il faisait 572 lignes
 * avant le découpage, et aurait dépassé 1200 sans lui.
 *
 * DEUX MODES, ET CHACUN RÉPOND À UNE QUESTION DIFFÉRENTE
 *
 *   Plan       « où le champ va-t-il s'écrire ? »   raster du PDF BRUT + zones éditables
 *   Rempli     « qu'est-ce que ça donne ? »          raster du PDF RÉELLEMENT PRODUIT
 *
 * ⚠️ IL Y EN AVAIT TROIS, ET LE TROISIÈME OUVRAIT L'ÉCRAN SUR LA MAUVAISE PAGE.
 *
 * `Document` montrait le PDF dans une `<iframe>` — et c'était le mode PAR DÉFAUT. On
 * importait donc un mandat, et on atterrissait sur un aperçu en lecture seule au lieu de
 * l'atelier où l'on pose les champs. Le geste courant demandait deux clics pour commencer.
 * Boubacar, le 09/09 : « je dois pouvoir uploader un nouveau mandat et remplir les cases
 * dans le même atelier ».
 *
 * ⚠️ MAIS L'`<iframe>` N'A PAS DISPARU : ELLE SERT LE MODÈLE SYSTÈME.
 *
 * Le mandat généré par le code n'a ni plan ni zones — il n'y a rien à poser dessus. C'est
 * l'`<iframe>` ou rien. La supprimer entièrement aurait laissé « Aucun aperçu » sur le
 * seul document qu'on ne peut pas remplacer. Elle vit donc là, et nulle part ailleurs.
 *
 * Et pour LIRE ou IMPRIMER un gabarit, le bouton « Ouvrir le PDF » ouvre le même rendu
 * dans un onglet du navigateur : un onglet d'écran en moins, une capacité intacte.
 *
 * ⚠️ LE PLAN MONTRE LE DOCUMENT BRUT À DESSEIN. On pose un champ SOUS un libellé
 * (« Nom : », « Adresse du site : »), et ces libellés disparaissent sous les valeurs dans
 * le rendu rempli. Poser sur le rendu rempli, ce serait viser une cible déjà recouverte.
 *
 * ⚠️ LE MODE REMPLI PASSE PAR UN VRAI RENDU SERVEUR. Dessiner les valeurs en HTML
 * par-dessus le raster brut mentirait : les métriques de la police du navigateur ne sont
 * pas celles de `helv` dans PyMuPDF, et c'est PRÉCISÉMENT sur le débordement qu'on veut
 * être cru.
 *
 * ⚠️ `gabarits-liste` garde son testid : c'est ce que `~/uat-flexcore/uat-gabarits.mjs`
 * interroge, et une recette qui existe ne se casse pas pour une réorganisation d'écran.
 */

// 400 ms après la dernière touche. Chaque rendu recompose le PDF côté serveur : relancer
// à chaque frappe le ferait travailler pour des états intermédiaires que personne ne
// regarde. Assez court pour qu'on ne se demande pas si l'écran a compris.
const ATTENTE_SAISIE_MS = 400;

const SYSTEME = '__systeme__';
const MODES = [['Plan', 'plan'], ['Rempli', 'rempli']];

function fmtTaille(octets) {
  if (octets == null) return '—';
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${(octets / 1024).toFixed(1)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(2)} Mo`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function MandatTemplatePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [modeles, setModeles] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [envoi, setEnvoi] = useState(false);
  const [survol, setSurvol] = useState(false);
  const [selection, setSelection] = useState(null);
  const [valeurs, setValeurs] = useState({});
  // ⚠️ LE PLAN, PAS L'APERÇU. C'est l'écran de travail : on arrive pour poser des
  // champs, pas pour relire un PDF. Voir l'en-tête.
  const [mode, setMode] = useState('plan');
  const [apercu, setApercu] = useState({ url: null, chargement: false, a: null });
  const [jetons, setJetons] = useState(null);
  // ⚠️ LA GÉOMÉTRIE EST DEMANDÉE UNE FOIS, PAS DEUX.
  //
  // Le plan et le rendu appelaient chacun `/pages` de leur côté — et depuis que les deux
  // panneaux restent montés, les deux partaient à chaque changement de gabarit. Deux
  // fois 37 ms et deux ouvertures du PDF côté serveur pour la même réponse. La
  // géométrie vit donc ici, et descend aux deux.
  const plan = usePagesDuGabarit(selection === SYSTEME ? null : selection);
  const [clients, setClients] = useState([]);
  const [choixClient, setChoixClient] = useState(false);
  const champFichier = useRef(null);
  // L'URL du blob précédent doit être révoquée à la main : sans cela chaque frappe laisse
  // un document en mémoire, et une session de réglage en accumule des dizaines.
  const urlPrecedente = useRef(null);

  // Le modele GENERIQUE actif : depuis C8a une piece dediee a un agregateur peut etre
  // active a cote, et l'en-tete parle de celle qui part a tous les autres mandats.
  const actif = useMemo(() => modeles.find((m) => m.active && !m.agregateur), [modeles]);
  const modeleAffiche = useMemo(
    () => (selection === SYSTEME ? null : modeles.find((m) => m.id === selection)),
    [selection, modeles]);
  const surSysteme = selection === SYSTEME;

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/mandat-templates');
      setModeles(data);
      // Le modèle ACTIF s'ouvre de lui-même : c'est celui qui part aux clients, donc
      // celui qu'on vient vérifier neuf fois sur dix.
      setSelection((s) => s || data.find((m) => m.active)?.id || SYSTEME);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chargement impossible');
    }
    setChargement(false);
  }, []);

  useEffect(() => { if (isAdmin) charger(); else setChargement(false); }, [isAdmin, charger]);

  const rechargerJetons = useCallback(() => {
    if (!selection || selection === SYSTEME) { setJetons(null); return; }
    api.get(`/admin/mandat-templates/${selection}/jetons`)
      .then(({ data }) => setJetons(data))
      .catch(() => setJetons(null));
  }, [selection]);

  useEffect(rechargerJetons, [rechargerJetons]);

  // ---- L'aperçu <iframe>, refait à chaque changement de modèle ou de valeur ----
  const rafraichir = useCallback(async () => {
    if (!selection) return;
    // ⚠️ SEUL LE MODÈLE SYSTÈME MONTE ENCORE L'<iframe>. Pour un gabarit, l'écran affiche
    // le Plan ou le Rendu : refaire le PDF côté serveur à chaque frappe travaillerait pour
    // un document que personne ne regarde.
    if (!surSysteme) return;
    setApercu((a) => ({ ...a, chargement: true }));
    try {
      const reponse = surSysteme
        ? await api.get('/admin/mandat-templates/systeme/apercu', { responseType: 'blob' })
        : await api.post(`/admin/mandat-templates/${selection}/apercu`, valeurs,
                         { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([reponse.data], { type: 'application/pdf' }));
      if (urlPrecedente.current) URL.revokeObjectURL(urlPrecedente.current);
      urlPrecedente.current = url;
      setApercu({ url, chargement: false, a: new Date() });
    } catch (e) {
      setApercu({ url: null, chargement: false, a: null });
      toast.error(e.response?.data?.detail || "L'aperçu n'a pas pu être produit");
    }
  }, [selection, valeurs, surSysteme]);

  useEffect(() => {
    const tic = setTimeout(rafraichir, ATTENTE_SAISIE_MS);
    return () => clearTimeout(tic);
  }, [rafraichir]);

  useEffect(() => () => {
    if (urlPrecedente.current) URL.revokeObjectURL(urlPrecedente.current);
  }, []);

  if (user && !isAdmin) return <Navigate to="/dashboard" replace />;

  // ---- Actions ----
  const importer = async (fichier) => {
    if (!fichier) return;
    if (!fichier.name.toLowerCase().endsWith('.pdf')) { toast.error('Un PDF est requis'); return; }
    setEnvoi(true);
    try {
      const fd = new FormData();
      fd.append('file', fichier);
      const { data } = await api.post('/admin/mandat-templates', fd,
                                      { headers: { 'Content-Type': 'multipart/form-data' } });
      const absents = data.jetons_absents || [];
      toast[absents.length ? 'warning' : 'success'](
        absents.length
          ? `Modèle importé — placeholders absents : ${absents.join(', ')}`
          : 'Modèle importé, tous les placeholders sont présents');
      setSelection(data.id);
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Le modèle n'a pas pu être importé");
    }
    setEnvoi(false);
  };

  const activer = async (id) => {
    try {
      await api.put(`/admin/mandat-templates/${id}/activate`);
      toast.success('Modèle activé');
      await charger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Activation impossible'); }
  };

  const revenirAuSysteme = async () => {
    try {
      await api.put('/admin/mandat-templates/deactivate-all');
      toast.success('Retour au modèle système');
      setSelection(SYSTEME);
      await charger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Opération impossible'); }
  };

  const supprimer = async (id) => {
    try {
      await api.delete(`/admin/mandat-templates/${id}`);
      toast.success('Modèle retiré');
      if (selection === id) setSelection(SYSTEME);
      await charger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Suppression impossible'); }
  };

  const ouvrirChoixClient = async () => {
    setChoixClient(true);
    if (clients.length) return;
    try {
      const { data } = await api.get('/clients');
      setClients((data || []).slice(0, 300));
    } catch { toast.error("La liste des clients n'a pas pu être chargée"); }
  };

  /** Le PDF rempli des valeurs de l'écran, ouvert dans un onglet — pour lire, imprimer. */
  const ouvrirLePdf = async () => {
    try {
      const reponse = await api.post(`/admin/mandat-templates/${selection}/apercu`, valeurs,
                                     { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([reponse.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      // ⚠️ ON NE RÉVOQUE PAS TOUT DE SUITE : l'onglet qui vient de s'ouvrir lit encore
      // cette URL. Une minute suffit largement au navigateur pour charger le document,
      // et laisse la mémoire se rendre au lieu de fuir à chaque clic.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Le PDF n'a pas pu être ouvert");
    }
  };

  const chargerClient = async (id) => {
    try {
      const { data } = await api.get(`/admin/mandat-templates/valeurs/${id}`);
      setValeurs(data.valeurs || {});
      setChoixClient(false);
      toast.success('Valeurs chargées depuis le dossier');
    } catch (e) { toast.error(e.response?.data?.detail || 'Dossier illisible'); }
  };

  if (chargement) {
    return (
      <div className="p-10 flex items-center gap-2 text-neutral-500" data-testid="gabarits-chargement">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
      </div>
    );
  }

  return (
    <div className="p-3 md:p-4 lg:p-6 space-y-3" data-testid="gabarits-atelier">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Breadcrumb items={[{ label: 'Paramètres' }, { label: 'Modèle de mandat' }]} />
          <h1 className="text-xl font-semibold tracking-tight text-neutral-900">
            Modèle de mandat
          </h1>
          <p className="text-[12px] text-neutral-400 mt-0.5">
            {actif
              ? <>Le modèle <strong className="text-neutral-600">{actif.nom_fichier}</strong> part
                aux clients depuis le {fmtDate(actif.active_le)}.</>
              : 'Aucun modèle personnalisé : le mandat généré par le code part aux clients.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={champFichier} type="file" accept="application/pdf" className="hidden"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) importer(f); e.target.value = ''; }}
                 data-testid="gabarit-champ-fichier" />
          {/* ⚠️ LE BOUTON D'IMPORT A DÉMÉNAGÉ DANS LA COLONNE DE GAUCHE.
              Il vivait ici, dans l'en-tête, à 1 070 px de la zone « Déposer un PDF » qui
              fait exactement la même chose. Deux commandes pour un geste, à deux bouts de
              l'écran : Boubacar l'a relevé le 10/09. Elles n'en font plus qu'une, posée
              là où l'œil cherche un modèle — dans la liste des modèles. */}
          {actif && (
            <Button variant="outline" size="sm" onClick={revenirAuSysteme}
                    data-testid="gabarit-systeme">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Modèle système
            </Button>
          )}
        </div>
      </div>

      {/* ⚠️ 144 px À GAUCHE ET 260 À DROITE, au lieu de 240 et 300 : sur un écran de
          1600 px, le plan gagne ~130 px de large. Et `calc(100vh - 190px)` au lieu de
          `72vh` rend ~15 % de verticale en plus. On pose des champs au pixel : chaque
          pixel de surface compte.

          ⚠️ ET LA HAUTEUR FIXE NE DESCEND PAS SOUS `lg`. Elle était posée en style inline,
          donc appliquée à TOUTES les tailles : sur une tablette, les trois panneaux
          s'empilaient à l'intérieur de 520 px et l'écran devenait illisible. Boubacar le
          09/09 : « ça s'affiche très mal sur des écrans type tablette ou téléphone ».
          Au-dessous de `lg`, la page reprend sa hauteur naturelle et défile. */}
      {modeleAffiche && <RattachementAgregateur gabarit={modeleAffiche} onChange={charger} />}
      <LienInscriptionAgregateur />

      <div className="grid grid-cols-1 lg:grid-cols-[144px_minmax(0,1fr)_260px] gap-3
                      lg:h-[calc(100vh-190px)] lg:min-h-[520px]">

        <ListeDesGabarits
          modeles={modeles} selection={selection} systeme={SYSTEME}
          survol={survol} setSurvol={setSurvol} envoi={envoi}
          onChoisir={setSelection} onActiver={activer} onSupprimer={supprimer}
          onImporter={importer} onCliquerImporter={() => champFichier.current?.click()}
          fmtDate={fmtDate} fmtTaille={fmtTaille}
        />

        {/* ⚠️ L'ORDRE D'EMPILEMENT N'EST PAS CELUI DES COLONNES. Empilé, on veut d'abord
            le DOCUMENT — c'est le sujet de l'écran — puis les valeurs, et la liste des
            modèles en dernier : on n'en change pas à chaque visite. `min-h-[60vh]` parce
            qu'un `flex-1` dans un conteneur de hauteur automatique se replie à zéro. */}
        <div className="flex flex-col gap-2 order-1 lg:order-2 min-h-[60vh] lg:min-h-0"
             data-testid="gabarits-apercu">
          <div className="flex items-center justify-between gap-3 flex-wrap shrink-0">
            <div className="min-w-0">
              <p className="text-[12px] font-medium text-neutral-900 truncate">
                {surSysteme ? 'Modèle système' : (modeleAffiche?.nom_fichier || '—')}
              </p>
              <p className="text-[10.5px] text-neutral-500">
                {surSysteme
                  ? 'Rendu sur un dossier fictif'
                  : <>{modeleAffiche?.active ? '● Actif · ' : ''}{modeleAffiche?.pages} page
                     {modeleAffiche?.pages > 1 ? 's' : ''} · {fmtTaille(modeleAffiche?.taille)}</>}
              </p>
            </div>
            {!surSysteme && (
              <div className="flex items-center gap-1 text-[11px]" data-testid="gabarit-mode">
                {/* ⚠️ L'ONGLET « Document » A DISPARU, PAS LA CAPACITÉ. Il était le seul
                    endroit où LIRE et IMPRIMER le PDF ; ce bouton l'ouvre dans un onglet du
                    navigateur, qui sait imprimer mieux qu'une <iframe> encastrée. */}
                <button type="button" onClick={ouvrirLePdf}
                        className="px-2.5 py-1 rounded-md border border-neutral-200
                                   text-neutral-500 hover:bg-neutral-50 mr-1"
                        data-testid="gabarit-ouvrir-pdf">
                  Ouvrir le PDF
                </button>
                {MODES.map(([libelle, cle]) => (
                  <button
                    type="button" key={cle}
                    onClick={() => setMode(cle)}
                    className={`px-2.5 py-1 rounded-md border ${
                      mode === cle
                        ? 'border-marque bg-marque/5 text-marque'
                        : 'border-neutral-200 text-neutral-500'}`}
                    data-testid={`gabarit-mode-${cle}`}
                  >
                    {libelle}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 rounded-lg border border-neutral-200 bg-neutral-50 overflow-hidden">
            {/* ⚠️ LES DEUX PANNEAUX RESTENT MONTÉS, ON EN CACHE UN. ET CE N'EST PAS UNE
                OPTIMISATION, C'EST UN CORRECTIF.

                Mesuré le 10/09 : on posait une zone, on passait sur « Rempli » pour voir
                ce que ça donnait, et le plan se DÉMONTAIT — emportant les zones non
                encore enregistrées, SANS un mot. On revenait, elles n'étaient plus là.
                C'est ce qui a produit le « j'ai mis des emplacements et rien n'apparaît »
                de la recette manuelle : les emplacements n'existaient plus au moment du
                rendu.

                ⚠️ ET ON N'ENREGISTRE PAS AUTOMATIQUEMENT À LA PLACE. L'historique des
                versions est APPEND-ONLY (ATL-3) : un enregistrement par changement
                d'onglet gonflerait de dizaines d'entrées une séance de réglage, et
                chacune est une apparence qu'un mandat pourrait rejouer. Le geste reste
                explicite ; c'est la perte silencieuse qui disparaît.

                Effet de bord bienvenu : revenir sur le plan ne recharge plus le raster
                des pages, déjà en mémoire. */}
            {!surSysteme ? (
              <>
                <div className="h-full" hidden={mode !== 'plan'}>
                  <PlanDeGabarit gabaritId={selection} plan={plan}
                                 onEnregistre={rechargerJetons} />
                </div>
                <div className="h-full" hidden={mode !== 'rempli'}>
                  <RenduRempli gabaritId={selection} pages={plan.pages}
                               valeurs={valeurs} actif={mode === 'rempli'} />
                </div>
              </>
            ) : apercu.url ? (
              <iframe title="Aperçu du modèle de mandat" src={apercu.url}
                      className="w-full h-full" data-testid="gabarit-iframe" />
            ) : (
              <div className="h-full flex items-center justify-center text-[12px] text-neutral-400">
                {apercu.chargement ? 'Rendu en cours…' : 'Aucun aperçu'}
              </div>
            )}
          </div>

          <p className="text-[10.5px] text-neutral-400 flex items-center gap-1.5 shrink-0"
             data-testid="gabarit-fraicheur">
            {surSysteme
              ? (apercu.chargement
                  ? <><Loader2 className="w-3 h-3 animate-spin" /> rendu en cours…</>
                  : apercu.a ? `mis à jour à ${apercu.a.toLocaleTimeString('fr-FR')}` : '')
              : mode === 'plan'
                ? 'plan du document brut · les libellés sous lesquels on pose les champs'
                : 'rendu réel du serveur · c’est ce que le PDF produira, débordements compris'}
          </p>

          <div className="shrink-0 max-h-[30%] overflow-y-auto">
            <CouvertureDesChamps jetons={jetons} />
          </div>
        </div>

        <ValeursDeLApercu
          catalogue={jetons?.champs_posables}
          jetons={jetons} valeurs={valeurs} setValeurs={setValeurs}
          desactive={surSysteme} clients={clients} choixClient={choixClient}
          onOuvrirChoixClient={ouvrirChoixClient} onChargerClient={chargerClient}
          onValeursExemple={() => setValeurs({})}
        />
      </div>
    </div>
  );
}

export { SYSTEME };
